/**
 * heroes.ts — Capas premium do Studio (composições)
 *
 * Cada capa aqui NÃO é um bloco fechado: é uma composição montada com a seção
 * com camadas (`section.layered`) e os primitivos (`ui.*`). Isso significa que
 * o usuário pode editar cada pedaço — selo, título, texto, botões, cartão,
 * prova social — direto no painel de propriedades, e que duas capas diferentes
 * compartilham as mesmas peças.
 *
 * Fotografias: banco gratuito Pexels (uso livre, sem atribuição obrigatória).
 *
 * RELEASE: v1.0.0 - 2026-09-10 - 20 capas iniciais (Studio, motor de modelos — Fase 3)
 */

import { registerPatterns } from './patternRegistry';
import type { StudioPatternDefinition, StudioPatternNode } from './types';
import type { StudioOutput } from '../types';

/** Atalho para montar um nó de padrão. */
function b(
  type: string,
  props: Record<string, unknown> = {},
  children?: StudioPatternNode[],
): StudioPatternNode {
  return children ? { type, props, children } : { type, props };
}

const WEB: StudioOutput[] = ['web'];

/** Monta o endereço de uma foto do Pexels na largura pedida (só avatares). */
function foto(id: number, largura = 1600): string {
  return `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${largura}`;
}

/**
 * Repertório de fotos usado pelas capas.
 * Guardamos a REFERÊNCIA do acervo compartilhado (`acervo://<chave>`), nunca o
 * endereço final: assim o painel WL pode trocar a foto sem reabrir os modelos.
 */
const FOTO = {
  cidadeNoite: 'acervo://capa.cidade-noite',
  reuniao: 'acervo://capa.reuniao',
  equipe: 'acervo://capa.equipe',
  escritorio: 'acervo://capa.escritorio',
  notebook: 'acervo://capa.notebook',
  painel: 'acervo://capa.painel',
  mesaTrabalho: 'acervo://capa.mesa-trabalho',
  atendimento: 'acervo://capa.atendimento',
  arquitetura: 'acervo://capa.arquitetura',
  palestra: 'acervo://capa.palestra',
  produto: 'acervo://capa.produto',
  loja: 'acervo://capa.loja',
  celular: 'acervo://capa.celular',
  criativo: 'acervo://capa.criativo',
  industria: 'acervo://capa.industria',
  consultoria: 'acervo://capa.consultoria',
  dados: 'acervo://capa.dados',
  interior: 'acervo://capa.interior',
  time: 'acervo://capa.time',
  laptopCodigo: 'acervo://capa.laptop-codigo',
  retrato: 'acervo://capa.retrato',
  minimal: 'acervo://capa.minimal',
  logistica: 'acervo://capa.logistica',
};

/** Avatares usados na prova social (fotos pequenas). */
const AVATARES = [
  `${foto(936137, 120)}|Cliente`,
  `${foto(3184465, 120)}|Cliente`,
  `${foto(1181244, 120)}|Cliente`,
].join('\n');

const capas: StudioPatternDefinition[] = [
  // 1 ------------------------------------------------------------- Cinema
  {
    key: 'capa.cinematografica',
    category: 'capa',
    label: 'Capa cinematográfica',
    description: 'Foto em tela cheia, escurecida, com título gigante.',
    preview: 'hero-image',
    outputs: WEB,
    order: 1,
    build: () => [
      b(
        'section.layered',
        {
          backgroundImage: FOTO.cidadeNoite,
          overlay: 'bottom',
          minHeight: 'screen',
          paddingY: 'xl',
          align: 'left',
          textColor: '#ffffff',
          layout: 'stack',
          maxWidth: '1120px',
          motion: 'fade',
        },
        [
          b('ui.badge', { text: 'Presente em todo o Brasil', variant: 'glass', motion: 'fade-up' }),
          b('ui.title', {
            text: 'Crescimento que se vê no resultado do mês',
            highlight: 'no resultado do mês',
            size: 'display',
            level: 'h1',
            accentColor: '#7dd3fc',
            motion: 'fade-up',
          }),
          b('ui.paragraph', {
            text: 'Estratégia, execução e acompanhamento próximo para a sua empresa vender mais sem aumentar a equipe.',
            size: 'lg',
            maxWidth: '620px',
            motion: 'fade-up',
          }),
          b('ui.buttons', {
            items: 'Falar com um especialista|#formulario|solid\nVer casos reais|#casos|glass',
            size: 'lg',
            motion: 'fade-up',
          }),
        ],
      ),
    ],
  },

  // 2 ------------------------------------------------------------- SaaS
  {
    key: 'capa.painel-sistema',
    category: 'capa',
    label: 'Capa com painel do sistema',
    description: 'Texto à esquerda e a tela do produto em moldura.',
    preview: 'hero-split',
    outputs: WEB,
    order: 2,
    build: () => [
      b(
        'section.layered',
        {
          backgroundGradient: 'mesh-brand',
          pattern: 'grid',
          layout: 'split',
          paddingY: 'lg',
          align: 'left',
          motion: 'fade',
        },
        [
          b('layout.section', { padding: '0px' }, [
            b('ui.badge', { text: 'Novo painel de resultados', prefix: '★', variant: 'soft' }),
            b('ui.title', {
              text: 'Toda a sua operação em uma tela só',
              highlight: 'uma tela só',
              size: 'xl',
              accentColor: '#2563eb',
              motion: 'fade-up',
            }),
            b('ui.paragraph', {
              text: 'Acompanhe vendas, atendimento e cobranças em tempo real, sem planilha e sem retrabalho.',
              motion: 'fade-up',
            }),
            b('ui.buttons', {
              items: 'Começar agora|#formulario|solid\nAgendar demonstração|#agenda|outline',
              motion: 'fade-up',
            }),
            b('ui.avatars', { items: AVATARES, text: '+2.000 empresas usando todo dia', note: 'Nota 4,9 de 5' }),
          ]),
          b('ui.device', { src: FOTO.painel, alt: 'Painel do sistema', device: 'browser', shadow: 'lg', motion: 'fade-up' }),
        ],
      ),
    ],
  },

  // 3 ------------------------------------------------------------- Dividida
  {
    key: 'capa.dividida-foto',
    category: 'capa',
    label: 'Capa dividida com foto',
    description: 'Metade texto, metade fotografia grande.',
    preview: 'split-right',
    outputs: WEB,
    order: 3,
    build: () => [
      b(
        'section.layered',
        { layout: 'split', paddingY: 'lg', align: 'left', backgroundColor: '#ffffff' },
        [
          b('layout.section', { padding: '0px' }, [
            b('ui.badge', { text: 'Atendimento humano', variant: 'outline' }),
            b('ui.title', { text: 'Perto de você em cada etapa', size: 'xl', motion: 'fade-up' }),
            b('ui.paragraph', {
              text: 'Um time dedicado para entender o seu momento e propor o caminho mais curto até o resultado.',
              motion: 'fade-up',
            }),
            b('ui.buttons', { items: 'Quero uma proposta|#formulario|solid', motion: 'fade-up' }),
          ]),
          b('ui.image', { src: FOTO.reuniao, alt: 'Reunião de equipe', ratio: 'portrait', radius: 'xl', shadow: 'lg', motion: 'scale' }),
        ],
      ),
    ],
  },

  // 4 ------------------------------------------------------------- Editorial
  {
    key: 'capa.editorial',
    category: 'capa',
    label: 'Capa editorial',
    description: 'Tipografia gigante, foto ampla abaixo.',
    preview: 'hero-center',
    outputs: WEB,
    order: 4,
    build: () => [
      b(
        'section.layered',
        { backgroundColor: '#faf7f2', paddingY: 'xl', align: 'center', textColor: '#1c1917' },
        [
          b('ui.badge', { text: 'Edição 2026', variant: 'outline', align: 'center' }),
          b('ui.title', {
            text: 'O jeito artesanal de fazer negócio',
            size: 'display',
            align: 'center',
            motion: 'fade-up',
          }),
          b('ui.paragraph', {
            text: 'Menos ruído, mais cuidado com cada detalhe do que entregamos.',
            size: 'lg',
            align: 'center',
            maxWidth: '620px',
            motion: 'fade-up',
          }),
          b('ui.image', { src: FOTO.interior, alt: 'Ambiente', ratio: 'wide', radius: 'lg', shadow: 'md', motion: 'reveal' }),
        ],
      ),
    ],
  },

  // 5 ------------------------------------------------------------- Evento
  {
    key: 'capa.evento',
    category: 'capa',
    label: 'Capa de evento',
    description: 'Data, local e palestrante em destaque.',
    preview: 'hero-split',
    outputs: WEB,
    order: 5,
    build: () => [
      b(
        'section.layered',
        {
          backgroundImage: FOTO.palestra,
          overlay: 'darker',
          layout: 'split',
          paddingY: 'xl',
          textColor: '#ffffff',
          align: 'left',
        },
        [
          b('layout.section', { padding: '0px' }, [
            b('ui.badge', { text: '18 de março · São Paulo', variant: 'glass' }),
            b('ui.title', { text: 'O encontro de quem faz o mercado girar', size: 'xl', motion: 'fade-up' }),
            b('ui.paragraph', { text: 'Um dia inteiro de conteúdo prático, com quem já colocou em pé o que ensina.', motion: 'fade-up' }),
            b('ui.buttons', { items: 'Garantir minha vaga|#formulario|solid\nVer programação|#programacao|glass', size: 'lg' }),
          ]),
          b('ui.card', {
            variant: 'glass',
            icon: '🎤',
            title: 'Últimos ingressos',
            text: 'Lotes anteriores esgotados. Reserve antes do encerramento.',
            radius: 'xl',
            shadow: 'glow',
            motion: 'floating',
          }),
        ],
      ),
    ],
  },

  // 6 ------------------------------------------------------------- Produto
  {
    key: 'capa.produto',
    category: 'capa',
    label: 'Capa de produto',
    description: 'Produto em destaque com fundo suave.',
    preview: 'hero-split',
    outputs: WEB,
    order: 6,
    build: () => [
      b(
        'section.layered',
        { backgroundGradient: 'soft', layout: 'split', paddingY: 'lg', glow: 'top-right' },
        [
          b('layout.section', { padding: '0px' }, [
            b('ui.badge', { text: 'Lançamento', variant: 'solid' }),
            b('ui.title', { text: 'Feito para durar, pensado no detalhe', size: 'xl', motion: 'fade-up' }),
            b('ui.paragraph', { text: 'Materiais selecionados, acabamento impecável e garantia estendida.', motion: 'fade-up' }),
            b('ui.buttons', { items: 'Comprar agora|#comprar|solid\nFalar no WhatsApp|#contato|outline' }),
          ]),
          b('ui.image', { src: FOTO.produto, alt: 'Produto', ratio: 'square', radius: 'xl', shadow: 'lg', motion: 'floating' }),
        ],
      ),
    ],
  },

  // 7 ------------------------------------------------------------- Bento
  {
    key: 'capa.bento',
    category: 'capa',
    label: 'Capa em mosaico',
    description: 'Texto grande com cartões ao lado, estilo bento.',
    preview: 'grid-3',
    outputs: WEB,
    order: 7,
    build: () => [
      b(
        'section.layered',
        { backgroundColor: '#0b1120', textColor: '#e2e8f0', layout: 'bento', paddingY: 'lg', glow: 'bottom-left' },
        [
          b('layout.section', { padding: '0px' }, [
            b('ui.badge', { text: 'Plataforma completa', variant: 'glass' }),
            b('ui.title', { text: 'Tudo o que a sua operação precisa', size: 'xl', motion: 'fade-up' }),
            b('ui.paragraph', { text: 'Vendas, atendimento, cobrança e relatórios no mesmo lugar.', motion: 'fade-up' }),
            b('ui.buttons', { items: 'Criar conta|#formulario|solid\nFalar com vendas|#contato|glass' }),
          ]),
          b('ui.card', { variant: 'glass', icon: '⚡', title: 'Rápido de implantar', text: 'Em poucos dias já rodando.', radius: 'xl' }),
          b('ui.card', { variant: 'glass', icon: '🔒', title: 'Seguro', text: 'Seus dados protegidos e auditados.', radius: 'xl' }),
        ],
      ),
    ],
  },

  // 8 ------------------------------------------------------------- Vidro
  {
    key: 'capa.vidro',
    category: 'capa',
    label: 'Capa de vidro',
    description: 'Cartão translúcido sobre fotografia.',
    preview: 'hero-image',
    outputs: WEB,
    order: 8,
    build: () => [
      b(
        'section.layered',
        {
          backgroundImage: FOTO.arquitetura,
          overlay: 'dark',
          paddingY: 'xl',
          minHeight: 'lg',
          textColor: '#ffffff',
          layout: 'split-wide-left',
        },
        [
          b('ui.card', {
            variant: 'glass',
            icon: '🏗️',
            title: 'Obras entregues no prazo',
            text: '312 projetos concluídos sem atraso nos últimos cinco anos.',
            radius: 'xl',
            shadow: 'glow',
            motion: 'floating',
          }),
          b('layout.section', { padding: '0px' }, [
            b('ui.title', { text: 'Projetos que ficam de pé por décadas', size: 'lg', motion: 'fade-up' }),
            b('ui.paragraph', { text: 'Planejamento, execução e entrega com prazo cumprido.', motion: 'fade-up' }),
            b('ui.buttons', { items: 'Pedir orçamento|#formulario|glass' }),
          ]),
        ],
      ),
    ],
  },

  // 9 ------------------------------------------------------------- Gradiente
  {
    key: 'capa.gradiente',
    category: 'capa',
    label: 'Capa em gradiente',
    description: 'Malha colorida, texto centralizado e prova social.',
    preview: 'hero-center',
    outputs: WEB,
    order: 9,
    build: () => [
      b(
        'section.layered',
        { backgroundGradient: 'mesh-dark', pattern: 'dots', paddingY: 'xl', align: 'center', textColor: '#f8fafc' },
        [
          b('ui.badge', { text: 'Feito no Brasil', variant: 'glass', align: 'center' }),
          b('ui.title', { text: 'Uma nova forma de vender todos os dias', size: 'display', align: 'center', motion: 'fade-up' }),
          b('ui.paragraph', {
            text: 'Automatize o repetitivo e deixe o time focado no que gera receita.',
            size: 'lg',
            align: 'center',
            maxWidth: '640px',
            motion: 'fade-up',
          }),
          b('ui.buttons', { items: 'Testar sem custo|#formulario|solid\nVer preços|#precos|glass', align: 'center', size: 'lg' }),
          b('ui.avatars', { items: AVATARES, text: 'Mais de 2.000 empresas confiam', align: 'center' }),
        ],
      ),
    ],
  },

  // 10 ------------------------------------------------------------ Luxo
  {
    key: 'capa.luxo',
    category: 'capa',
    label: 'Capa minimalista de luxo',
    description: 'Muito espaço, tipografia fina e foto discreta.',
    preview: 'hero-center',
    outputs: WEB,
    order: 10,
    build: () => [
      b(
        'section.layered',
        { backgroundColor: '#111111', textColor: '#f5f5f4', paddingY: 'xl', align: 'center', minHeight: 'lg' },
        [
          b('ui.badge', { text: 'Atendimento exclusivo', variant: 'outline', align: 'center' }),
          b('ui.title', { text: 'Discrição, cuidado e resultado', size: 'xl', align: 'center', motion: 'fade' }),
          b('ui.paragraph', {
            text: 'Poucos clientes por vez, para que cada projeto receba a atenção que merece.',
            align: 'center',
            maxWidth: '520px',
            motion: 'fade',
          }),
          b('ui.buttons', { items: 'Solicitar convite|#formulario|outline', align: 'center' }),
        ],
      ),
    ],
  },

  // 11 ------------------------------------------------------------ Captura
  {
    key: 'capa.captura',
    category: 'capa',
    label: 'Capa com formulário',
    description: 'Texto à esquerda e formulário de contato à direita.',
    preview: 'form',
    outputs: WEB,
    templateTypes: ['landing_page'],
    order: 11,
    build: () => [
      b(
        'section.layered',
        { backgroundGradient: 'mesh-brand', layout: 'split', paddingY: 'lg', align: 'left' },
        [
          b('layout.section', { padding: '0px' }, [
            b('ui.badge', { text: 'Diagnóstico gratuito', variant: 'soft' }),
            b('ui.title', { text: 'Descubra quanto a sua empresa deixa na mesa', size: 'lg', motion: 'fade-up' }),
            b('ui.paragraph', { text: 'Preencha os dados e receba uma análise em até um dia útil.', motion: 'fade-up' }),
            b('ui.avatars', { items: AVATARES, text: 'Mais de 500 diagnósticos entregues' }),
          ]),
          b('page.form', {
            title: 'Fale com a gente',
            submitLabel: 'Quero meu diagnóstico',
            description: 'Responderemos no mesmo dia útil.',
          }),
        ],
      ),
    ],
  },

  // 12 ------------------------------------------------------------ Vídeo
  {
    key: 'capa.video',
    category: 'capa',
    label: 'Capa com vídeo',
    description: 'Apresentação em vídeo ao lado da chamada.',
    preview: 'video',
    outputs: WEB,
    order: 12,
    build: () => [
      b(
        'section.layered',
        { backgroundColor: '#0f172a', textColor: '#e2e8f0', layout: 'split', paddingY: 'lg', glow: 'center' },
        [
          b('layout.section', { padding: '0px' }, [
            b('ui.badge', { text: 'Veja em 90 segundos', variant: 'glass' }),
            b('ui.title', { text: 'Entenda como funciona na prática', size: 'lg', motion: 'fade-up' }),
            b('ui.paragraph', { text: 'Sem promessa vazia: mostramos o passo a passo real do serviço.', motion: 'fade-up' }),
            b('ui.buttons', { items: 'Falar com o time|#formulario|solid' }),
          ]),
          b('ui.video', { videoUrl: 'https://www.youtube.com/embed/ScMzIvxBSi4', radius: 'xl', shadow: 'lg' }),
        ],
      ),
    ],
  },

  // 13 ------------------------------------------------------------ Celular
  {
    key: 'capa.aplicativo',
    category: 'capa',
    label: 'Capa de aplicativo',
    description: 'Celular em moldura com chamada de instalação.',
    preview: 'hero-split',
    outputs: WEB,
    order: 13,
    build: () => [
      b(
        'section.layered',
        { backgroundGradient: 'aurora', layout: 'split', paddingY: 'lg', textColor: '#ecfeff' },
        [
          b('layout.section', { padding: '0px' }, [
            b('ui.badge', { text: 'Disponível para celular', variant: 'glass' }),
            b('ui.title', { text: 'Sua empresa no bolso do cliente', size: 'xl', motion: 'fade-up' }),
            b('ui.paragraph', { text: 'Pedidos, agenda e avisos em tempo real, direto no aparelho.', motion: 'fade-up' }),
            b('ui.buttons', { items: 'Baixar agora|#download|solid\nVer recursos|#recursos|glass' }),
          ]),
          b('ui.device', { src: FOTO.celular, alt: 'Aplicativo no celular', device: 'phone', shadow: 'lg', motion: 'floating' }),
        ],
      ),
    ],
  },

  // 14 ------------------------------------------------------------ Flutuante
  {
    key: 'capa.cartoes-flutuantes',
    category: 'capa',
    label: 'Capa com cartões flutuantes',
    description: 'Foto ao fundo e indicadores em cartões.',
    preview: 'hero-image',
    outputs: WEB,
    order: 14,
    build: () => [
      b(
        'section.layered',
        {
          backgroundImage: FOTO.escritorio,
          overlay: 'left',
          layout: 'split',
          paddingY: 'xl',
          textColor: '#ffffff',
          minHeight: 'lg',
        },
        [
          b('layout.section', { padding: '0px' }, [
            b('ui.title', { text: 'Resultados que aparecem no caixa', size: 'xl', motion: 'fade-up' }),
            b('ui.paragraph', { text: 'Processos claros, metas acompanhadas de perto e time engajado.', motion: 'fade-up' }),
            b('ui.buttons', { items: 'Quero esse resultado|#formulario|solid' }),
          ]),
          b('layout.columns', { count: 1, gap: '16px' }, [
            b('ui.card', { variant: 'glass', title: '+38% de vendas', text: 'Média dos clientes em 6 meses.', radius: 'xl', motion: 'floating' }),
            b('ui.card', { variant: 'glass', title: '-27% de custo', text: 'Menos retrabalho na operação.', radius: 'xl', motion: 'floating' }),
          ]),
        ],
      ),
    ],
  },

  // 15 ------------------------------------------------------------ Prova social
  {
    key: 'capa.prova-social',
    category: 'capa',
    label: 'Capa com prova social',
    description: 'Chamada central, avaliações e logos de clientes.',
    preview: 'logos',
    outputs: WEB,
    order: 15,
    build: () => [
      b(
        'section.layered',
        { backgroundColor: '#ffffff', paddingY: 'lg', align: 'center' },
        [
          b('ui.badge', { text: 'Nota 4,9 de 5', prefix: '★', variant: 'soft', align: 'center' }),
          b('ui.title', { text: 'Quem contrata, indica', size: 'xl', align: 'center', motion: 'fade-up' }),
          b('ui.paragraph', {
            text: 'Mais de 2.000 empresas atendidas com acompanhamento próximo do começo ao fim.',
            align: 'center',
            maxWidth: '600px',
            motion: 'fade-up',
          }),
          b('ui.buttons', { items: 'Falar com um especialista|#formulario|solid', align: 'center' }),
          b('ui.logos', { note: 'Empresas que confiam', align: 'center', grayscale: true }),
        ],
      ),
    ],
  },

  // 16 ------------------------------------------------------------ Loja
  {
    key: 'capa.loja',
    category: 'capa',
    label: 'Capa de loja',
    description: 'Oferta com foto do ambiente e chamada de compra.',
    preview: 'hero-split',
    outputs: WEB,
    order: 16,
    build: () => [
      b(
        'section.layered',
        { backgroundColor: '#fff7ed', layout: 'split-reverse', paddingY: 'lg', textColor: '#431407' },
        [
          b('ui.image', { src: FOTO.loja, alt: 'Loja', ratio: 'landscape', radius: 'xl', shadow: 'md', motion: 'scale' }),
          b('layout.section', { padding: '0px' }, [
            b('ui.badge', { text: 'Frete grátis nesta semana', variant: 'solid', accentColor: '#ea580c' }),
            b('ui.title', { text: 'Novidades chegando toda semana', size: 'lg', motion: 'fade-up' }),
            b('ui.paragraph', { text: 'Escolha, peça pelo site e retire na loja no mesmo dia.', motion: 'fade-up' }),
            b('ui.buttons', { items: 'Ver catálogo|#catalogo|solid\nFalar no WhatsApp|#contato|outline', accentColor: '#ea580c' }),
          ]),
        ],
      ),
    ],
  },

  // 17 ------------------------------------------------------------ Corporativa
  {
    key: 'capa.corporativa',
    category: 'capa',
    label: 'Capa corporativa',
    description: 'Sóbria, com números de credibilidade.',
    preview: 'stats',
    outputs: WEB,
    order: 17,
    build: () => [
      b(
        'section.layered',
        { backgroundImage: FOTO.time, overlay: 'darker', paddingY: 'xl', textColor: '#ffffff', align: 'left' },
        [
          b('ui.badge', { text: 'Desde 2004 no mercado', variant: 'outline' }),
          b('ui.title', { text: 'Solidez para decisões de longo prazo', size: 'xl', motion: 'fade-up' }),
          b('ui.paragraph', { text: 'Governança, previsibilidade e um time que conhece o seu setor.', maxWidth: '600px', motion: 'fade-up' }),
          b('layout.columns', { count: 3, gap: '20px' }, [
            b('ui.stat', { value: '20+', label: 'anos de estrada' }),
            b('ui.stat', { value: '2.000', label: 'clientes ativos' }),
            b('ui.stat', { value: '98%', label: 'de renovação' }),
          ]),
          b('ui.buttons', { items: 'Falar com o comercial|#formulario|solid' }),
        ],
      ),
    ],
  },

  // 18 ------------------------------------------------------------ Agência
  {
    key: 'capa.agencia',
    category: 'capa',
    label: 'Capa de agência criativa',
    description: 'Composição assimétrica, cor forte e movimento.',
    preview: 'hero-split',
    outputs: WEB,
    order: 18,
    build: () => [
      b(
        'section.layered',
        { backgroundGradient: 'sunset', layout: 'split-wide-left', paddingY: 'xl', textColor: '#fff7ed', pattern: 'rays' },
        [
          b('layout.section', { padding: '0px' }, [
            b('ui.title', { text: 'Ideias que as pessoas lembram', size: 'display', motion: 'reveal' }),
            b('ui.paragraph', { text: 'Marca, conteúdo e campanhas com a mesma assinatura criativa.', size: 'lg', motion: 'fade-up' }),
            b('ui.buttons', { items: 'Ver portfólio|#portfolio|glass\nComeçar um projeto|#formulario|solid' }),
          ]),
          b('ui.image', { src: FOTO.criativo, alt: 'Trabalho criativo', ratio: 'portrait', radius: 'xl', shadow: 'glow', motion: 'floating' }),
        ],
      ),
    ],
  },

  // 19 ------------------------------------------------------------ Tech escuro
  {
    key: 'capa.tech-escura',
    category: 'capa',
    label: 'Capa tecnológica escura',
    description: 'Fundo escuro, grade e código em destaque.',
    preview: 'hero-split',
    outputs: WEB,
    order: 19,
    build: () => [
      b(
        'section.layered',
        { backgroundColor: '#05070f', textColor: '#e2e8f0', pattern: 'grid', glow: 'top-right', layout: 'split', paddingY: 'lg' },
        [
          b('layout.section', { padding: '0px' }, [
            b('ui.badge', { text: 'Integração em minutos', variant: 'glass' }),
            b('ui.title', { text: 'Conecte tudo, sem quebrar nada', highlight: 'sem quebrar nada', accentColor: '#38bdf8', size: 'xl', motion: 'fade-up' }),
            b('ui.paragraph', { text: 'Documentação clara, ambiente de teste e suporte técnico de verdade.', motion: 'fade-up' }),
            b('ui.buttons', { items: 'Ver documentação|#docs|glass\nFalar com engenharia|#formulario|solid' }),
          ]),
          b('ui.image', { src: FOTO.laptopCodigo, alt: 'Código', ratio: 'landscape', radius: 'xl', shadow: 'glow', motion: 'fade-up' }),
        ],
      ),
    ],
  },

  // 20 ------------------------------------------------------------ Serviço local
  {
    key: 'capa.servico-local',
    category: 'capa',
    label: 'Capa de serviço local',
    description: 'Foto de campo, área de atendimento e contato rápido.',
    preview: 'hero-image',
    outputs: WEB,
    order: 20,
    build: () => [
      b(
        'section.layered',
        { backgroundImage: FOTO.logistica, overlay: 'bottom', paddingY: 'xl', minHeight: 'md', textColor: '#ffffff' },
        [
          b('ui.badge', { text: 'Atendimento em toda a região', prefix: '📍', variant: 'glass' }),
          b('ui.title', { text: 'Chegamos rápido quando você precisa', size: 'xl', motion: 'fade-up' }),
          b('ui.paragraph', { text: 'Equipe própria, frota rastreada e prazo combinado antes de começar.', maxWidth: '580px', motion: 'fade-up' }),
          b('ui.buttons', { items: 'Chamar agora|#contato|solid\nPedir orçamento|#formulario|glass', size: 'lg' }),
        ],
      ),
    ],
  },
];

registerPatterns(capas);
